"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBulkMoveAssets,
  useSplitTreatments,
  type BulkMoveResult,
} from "@zapier/client";
import { getFolderUrl, useNotification } from "@zapier/utils";
import { Alert, Link, Spinner, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { MoveModalInner } from "./core/MoveModalInner";
import type { OnMoveOptions } from "./core/types";
import { TransferOwnershipPrompt } from "./TransferOwnershipPrompt";
import styles from "./core/MoveModal.module.css";

const FOLDER_PREFIX = "folder:";

interface PermissionResult {
  movableIds: string[];
  nonMovableCount: number;
  isLoading: boolean;
  hasChecked: boolean;
}

interface PendingOwnerTransfer {
  result: BulkMoveResult & {
    ownerMismatch: NonNullable<BulkMoveResult["ownerMismatch"]>;
  };
  selectedFolderId: string;
  selectedFolderName: string;
}

interface BulkMoveModalProps {
  selectedAssetIds: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSettled?: () => void;
  folderId?: string;
  permissionResult: PermissionResult;
}

export function BulkMoveModal({
  selectedAssetIds,
  isOpen,
  setIsOpen,
  onSettled,
  folderId,
  permissionResult,
}: BulkMoveModalProps) {
  const t = useTranslations("move-asset-actions");
  const { createNotification } = useNotification();
  const { bulkMoveAssets } = useBulkMoveAssets({ folderId });
  const { isPrivateFolderOwnerPolicyEnabled } = useSplitTreatments();
  const [pendingOwnerTransfer, setPendingOwnerTransfer] = useState<
    PendingOwnerTransfer | undefined
  >();
  const [isMoving, setIsMoving] = useState(false);
  // Set when a transfer is cancelled: the Zaps still waiting to move, without
  // the folders that already moved before the Zaps were refused.
  const [retryIds, setRetryIds] = useState<string[] | undefined>();
  // Bumped whenever the dialog closes, so a reply to a request started in an
  // earlier session cannot reopen the prompt for a different selection.
  const sessionReference = useRef(0);

  useEffect(() => {
    if (isOpen) return;
    sessionReference.current += 1;
    setPendingOwnerTransfer(undefined);
    setIsMoving(false);
    setRetryIds(undefined);
  }, [isOpen]);

  const {
    movableIds: permittedIds,
    nonMovableCount,
    isLoading: isPermLoading,
    hasChecked,
  } = permissionResult;
  const movableIds = retryIds ?? permittedIds;

  const noneMovable =
    hasChecked && movableIds.length === 0 && selectedAssetIds.length > 0;

  // Extract raw folder IDs from the selection so we can block users from
  // picking one of the selected folders as the destination (cycle prevention).
  // We also reuse these source folder IDs to evaluate source-folder visibility
  // rules for hiding private folders.
  const disabledFolderIds = useMemo(
    () =>
      movableIds
        .filter((id) => id.startsWith(FOLDER_PREFIX))
        .map((id) => id.slice(FOLDER_PREFIX.length)),
    [movableIds],
  );

  const headerOverride = useMemo(() => {
    if (noneMovable) return t("bulk-move.header-no-permission");
    const count =
      !hasChecked || isPermLoading
        ? selectedAssetIds.length
        : movableIds.length;
    return t("bulk-move.header-move-count", { count });
  }, [
    hasChecked,
    isPermLoading,
    movableIds.length,
    noneMovable,
    selectedAssetIds.length,
    t,
  ]);

  const bodyAlert = useMemo(() => {
    if (isPermLoading) {
      return (
        <div className={styles.bulkSpinner}>
          <Spinner />
        </div>
      );
    }
    if (nonMovableCount === 0) return;
    const message = noneMovable
      ? t("bulk-move.alert-no-permission")
      : t("bulk-move.alert-partial-permission", {
          movableCount: movableIds.length,
          totalCount: selectedAssetIds.length,
        });
    return (
      <div className={styles.bulkAlert}>
        <Alert variant="default" status="warning">
          {message}
        </Alert>
      </div>
    );
  }, [
    isPermLoading,
    movableIds.length,
    noneMovable,
    nonMovableCount,
    selectedAssetIds.length,
    t,
  ]);

  const notifyResult = useCallback(
    (
      result: BulkMoveResult,
      selectedFolderId: string,
      selectedFolderName: string,
    ) => {
      if (result.failedCount === 0) {
        createNotification({
          message: (
            <Text>
              {t.rich("notifications.move-success", {
                assetTitle: `${result.movedCount} items`,
                isBulk: "true",
                selectedFolderName,
                folderLink: (chunks) => (
                  <Link href={getFolderUrl(selectedFolderId)}>{chunks}</Link>
                ),
              })}
            </Text>
          ),
          type: "success",
        });
      } else {
        createNotification({
          message: t("bulk-move.partial-success", {
            movedCount: result.movedCount,
            totalCount: result.totalRequested,
          }),
          type: "warning",
        });
      }
    },
    [createNotification, t],
  );

  const notifyError = useCallback(() => {
    createNotification({
      title: t("bulk-move.error-title"),
      message: t("bulk-move.error-message"),
      type: "error",
    });
  }, [createNotification, t]);

  const finish = useCallback(() => {
    setPendingOwnerTransfer(undefined);
    setIsOpen(false);
    onSettled?.();
  }, [onSettled, setIsOpen]);

  const handleMove = useCallback(
    async ({ selectedFolderId, selectedFolderName }: OnMoveOptions) => {
      if (!selectedFolderId || movableIds.length === 0) return;

      if (!isPrivateFolderOwnerPolicyEnabled) {
        setIsOpen(false);
        try {
          const result = await bulkMoveAssets(movableIds, selectedFolderId);
          notifyResult(result, selectedFolderId, selectedFolderName);
        } catch {
          notifyError();
        }
        onSettled?.();
        return;
      }

      // A reply that lands after this dialog closed must not touch the dialog
      // the user may have opened since; it only reports what already moved.
      const session = sessionReference.current;
      const isCurrent = () => sessionReference.current === session;
      setIsMoving(true);
      try {
        const result = await bulkMoveAssets(movableIds, selectedFolderId, {
          privateFolderOwnerPolicy: "refuse",
        });
        if (result.ownerMismatch && isCurrent()) {
          setPendingOwnerTransfer({
            result: { ...result, ownerMismatch: result.ownerMismatch },
            selectedFolderId,
            selectedFolderName,
          });
          return;
        }
        if (
          !result.ownerMismatch ||
          result.movedCount > 0 ||
          result.failedCount > 0
        ) {
          notifyResult(result, selectedFolderId, selectedFolderName);
        }
      } catch {
        notifyError();
      } finally {
        if (isCurrent()) setIsMoving(false);
      }
      if (isCurrent()) finish();
    },
    [
      bulkMoveAssets,
      finish,
      isPrivateFolderOwnerPolicyEnabled,
      movableIds,
      notifyError,
      notifyResult,
      onSettled,
      setIsOpen,
    ],
  );

  const handleConfirmOwnerTransfer = useCallback(async () => {
    if (!pendingOwnerTransfer) return;
    const { result, selectedFolderId, selectedFolderName } =
      pendingOwnerTransfer;
    const session = sessionReference.current;
    const isCurrent = () => sessionReference.current === session;

    setIsMoving(true);
    try {
      const transferred = await bulkMoveAssets(
        result.ownerMismatch.assetIds,
        selectedFolderId,
        { privateFolderOwnerPolicy: "transfer" },
      );
      notifyResult(
        {
          totalRequested: result.totalRequested,
          movedCount: result.movedCount + transferred.movedCount,
          failedCount: result.failedCount + transferred.failedCount,
        },
        selectedFolderId,
        selectedFolderName,
      );
    } catch {
      notifyError();
    } finally {
      if (isCurrent()) setIsMoving(false);
    }
    if (isCurrent()) finish();
  }, [bulkMoveAssets, finish, notifyError, notifyResult, pendingOwnerTransfer]);

  // Folders in the selection moved before the Zaps were refused, so report
  // them even though the Zaps stay put. The picker stays open for a new choice.
  const handleCancelOwnerTransfer = useCallback(() => {
    if (!pendingOwnerTransfer) return;
    const { result, selectedFolderId, selectedFolderName } =
      pendingOwnerTransfer;
    if (result.movedCount > 0 || result.failedCount > 0) {
      notifyResult(result, selectedFolderId, selectedFolderName);
    }
    setRetryIds(result.ownerMismatch.assetIds);
    setPendingOwnerTransfer(undefined);
  }, [notifyResult, pendingOwnerTransfer]);

  if (!isOpen) return;

  if (pendingOwnerTransfer) {
    return (
      <TransferOwnershipPrompt
        folderName={pendingOwnerTransfer.selectedFolderName}
        folderOwnerId={pendingOwnerTransfer.result.ownerMismatch.folderOwnerId}
        assetCount={pendingOwnerTransfer.result.ownerMismatch.assets.length}
        isPending={isMoving}
        onCancel={handleCancelOwnerTransfer}
        onConfirm={handleConfirmOwnerTransfer}
      />
    );
  }

  return (
    <MoveModalInner
      assetIds={movableIds}
      sourceFolderIds={disabledFolderIds}
      assetParentId={folderId}
      bodyAlert={bodyAlert}
      disabledFolderIds={disabledFolderIds}
      forceSubmitDisabled={noneMovable || isPermLoading || isMoving}
      headerOverride={headerOverride}
      submitDisabledTooltip={
        noneMovable ? t("bulk-move.header-no-permission") : undefined
      }
      onMove={handleMove}
      setIsOpen={setIsOpen}
    />
  );
}
